import {
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Provider,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormControl, FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import {
  AccordionComponent,
  AccordionPanelComponent,
  AlertComponent,
  ButtonComponent,
  FormGroupComponent,
  FormsUtil,
  MbsLabelSize,
  MbsSize,
  ModalService,
  ModalSettings,
  RadioComponent,
  RadioDescription,
  SelectComponent
} from 'mbs-ui-kit';
import { BetaFeatureRequestComponent } from 'mbs-ui-shared@components/beta-feature-request/beta-feature-request.component';
import { MBSLicenseKind } from 'mbs-ui-shared@components/components/models/license.model';
import { RequestPermissionsModalComponent } from 'mbs-ui-shared@components/request-permissions-modal/request-permissions.component';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { ComputerModalsFacade } from 'mbs-ui-shared@facades/computer.modals.facade';
import { LicensesFacade } from 'mbs-ui-shared@facades/licenses.facade';
import { Administrator, LicenseType, PermissionsEnum, RestoreTargetEnum, StorageType } from 'mbs-ui-shared@models';
import Computer, { AgentType, PlatformTypes } from 'mbs-ui-shared@models/Computer';
import { BetaFeatureBuildName } from 'mbs-ui-shared@services/beta-request.service';
import { first, take } from 'rxjs/operators';
import { TargetComputerData } from 'wizards/models/base/base-plan-models';
import { ForceValidType } from 'wizards/models/base/other-base-models/force-valid-type';
import { RestoreAsDiskEnum, TypeDataStepValue, TypeDataStepValueForm } from 'wizards/models/type-data-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { StepBase } from 'wizards/steps/StepBase.class';

const VersionForVMTypes = 871;
const VersionForVMInstantTypes = 875;

const TypeDataStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => TypeDataStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-type-data-step',
  templateUrl: './type-data-step.component.html',
  styleUrls: ['./type-data-step.component.scss'],
  providers: [TypeDataStepValueAccessor],
  imports: [
    AccordionComponent,
    AccordionPanelComponent,
    AlertComponent,
    I18NextPipe,
    BetaFeatureRequestComponent,
    NgbTooltipModule,
    RadioComponent,
    RadioDescription,
    ReactiveFormsModule,
    SelectComponent,
    FormGroupComponent,
    ButtonComponent
  ]
})
export class TypeDataStepComponent extends StepBase<TypeDataStepValue> implements OnInit, OnChanges, OnDestroy {
  mainService: RemoteManagementWizardsService;
  private ability = inject(AbilityService);
  private auth = inject(AuthFacade);
  private licensesFacade = inject(LicensesFacade);
  private i18nPipe = inject(I18NextPipe);
  private modalFacade = inject(ComputerModalsFacade);
  private modalService = inject(ModalService);
  private stepService = inject(WizardStepsService);

  private readonly initialTargetComputerData: TargetComputerData = this.mainService?.targetComputerData;

  @ViewChild('restoreTypeStepAccordion') restoreTypeStepAccordion: AccordionComponent;

  @Input() fileFormats = [];
  @Input() selectedComputersCount = 0;
  @Output() restoreAsChange = new EventEmitter();

  public readonly mbsLabelSize = MbsLabelSize;
  public readonly mbsSize = MbsSize;
  public readonly restoreAsDiskEnum = RestoreAsDiskEnum;
  public readonly betaFeatureName = {
    instantRestoreToVirtualizationPlatforms: 'instant-restore-to-virtualization-platforms',
    restoreToVirtualizationPlatforms: 'restore-to-virtualization-platforms',
    restoreToCloud: 'restore-to-cloud'
  };
  public readonly betaFeatureBuildName = {
    backupBuilds: BetaFeatureBuildName.BackupBuilds
  };
  public readonly betaFeatureVersion = {
    instantRestoreToVirtualizationPlatforms: VersionForVMInstantTypes,
    restoreToCloud: VersionForVMInstantTypes,
    restoreToVirtualizationPlatforms: VersionForVMTypes
  };
  public readonly elementsSelector = {
    id: {
      physicalOrVirtualPanel: 'physical-or-virtual-panel',
      virtualizationPlatformsPanel: 'virtualization-platforms-panel',
      instantPanel: 'instant-panel',
      cloudPanel: 'cloud-panel',
      itemLvl: 'item-lvl-panel'
    },
    name: {
      restoreHyperV: 'restoreHyperV',
      restoreVMWare: 'restoreVMWare',
      restoreProxmox: 'restoreProxmox',
      notVMRestore: 'notVMRestore',
      RestoreToVMwareVSphere: 'RestoreToVMwareVSphere',
      RestoreToProxmox: 'RestoreToProxmox',
      RestoreToHyperV: 'RestoreToHyperV',
      InstantRestoreToHyperV: 'InstantRestoreToHyperV',
      InstantRestoreToProxmox: 'InstantRestoreToProxmox',
      InstantRestoreToDRServer: 'InstantRestoreToDRServer',
      RestoreToEC2: 'RestoreToEC2',
      RestoreToAzure: 'RestoreToAzure',
      multipleMachineAlert: 'multipleMachineAlert'
    }
  };
  public readonly restoreAsPanelActiveMap: Partial<Record<RestoreAsDiskEnum, [string]>> = {
    [RestoreAsDiskEnum.Physical]: [this.elementsSelector.id.physicalOrVirtualPanel],
    [RestoreAsDiskEnum.Virtual]: [this.elementsSelector.id.physicalOrVirtualPanel],

    [RestoreAsDiskEnum.RestoreToProxmox]: [this.elementsSelector.id.virtualizationPlatformsPanel],
    [RestoreAsDiskEnum.RestoreToVMware]: [this.elementsSelector.id.virtualizationPlatformsPanel],
    [RestoreAsDiskEnum.RestoreToVMwareVSphere]: [this.elementsSelector.id.virtualizationPlatformsPanel],
    [RestoreAsDiskEnum.RestoreToHyperV]: [this.elementsSelector.id.virtualizationPlatformsPanel],
    [RestoreAsDiskEnum.RestoreToHyperVAndImport]: [this.elementsSelector.id.virtualizationPlatformsPanel],

    [RestoreAsDiskEnum.InstantRestoreToHyperV]: [this.elementsSelector.id.instantPanel],
    [RestoreAsDiskEnum.InstantRestoreToProxmox]: [this.elementsSelector.id.instantPanel],
    [RestoreAsDiskEnum.InstantRestoreToDRServer]: [this.elementsSelector.id.instantPanel],

    [RestoreAsDiskEnum.EC2]: [this.elementsSelector.id.cloudPanel],
    [RestoreAsDiskEnum.AVM]: [this.elementsSelector.id.cloudPanel]
  };
  public readonly selectedInnerMap: { [key: string]: Partial<Record<RestoreAsDiskEnum, true>> } = {
    [this.elementsSelector.id.physicalOrVirtualPanel]: { [RestoreAsDiskEnum.Physical]: true, [RestoreAsDiskEnum.Virtual]: true },
    [this.elementsSelector.id.virtualizationPlatformsPanel]: {
      [RestoreAsDiskEnum.RestoreToProxmox]: true,
      [RestoreAsDiskEnum.RestoreToVMware]: true,
      [RestoreAsDiskEnum.RestoreToVMwareVSphere]: true,
      [RestoreAsDiskEnum.RestoreToHyperV]: true,
      [RestoreAsDiskEnum.RestoreToHyperVAndImport]: true
    },
    [this.elementsSelector.id.instantPanel]: {
      [RestoreAsDiskEnum.InstantRestoreToHyperV]: true,
      [RestoreAsDiskEnum.InstantRestoreToProxmox]: true,
      [RestoreAsDiskEnum.InstantRestoreToDRServer]: true
    },
    [this.elementsSelector.id.cloudPanel]: { [RestoreAsDiskEnum.EC2]: true, [RestoreAsDiskEnum.AVM]: true }
  };
  private readonly claudeRestoreTypes = [RestoreAsDiskEnum.EC2, RestoreAsDiskEnum.AVM];

  public activeId: string;
  public activeIds: string[] = [this.elementsSelector.id.physicalOrVirtualPanel];

  public currentUser: Administrator;

  public canBuyLicense = false;
  public canRestoreToVM = false;
  public canRestoreInstantToVM = false;
  public canRestoreToCloud = false;
  public canShowItemLvlPanel = false;
  public canRestoreToCloudStorage = false;
  public hasRestoreToCloudLicense = false;
  public canRestoreToAzure = false;
  public canRestoreToEC2 = false;

  private updateCount = 0;
  private typesForValidate = [
    RestoreAsDiskEnum.RestoreToHyperVAndImport,
    RestoreAsDiskEnum.RestoreToHyperV,
    RestoreAsDiskEnum.InstantRestoreToHyperV
  ];

  get disableRestoreAsVirtual(): boolean {
    return (
      this.isEdit &&
      this.isVMRestore &&
      this.stepForm?.get('restoreAs')?.value !== RestoreAsDiskEnum.Virtual &&
      !this.mainService.canChangeRestoreVMType
    );
  }

  get disableRestoreAsNotVirtual(): boolean {
    return this.isEdit && this.stepForm?.get('restoreAs')?.value === RestoreAsDiskEnum.Virtual && !this.mainService.canChangeRestoreVMType;
  }

  get isCloudStorageSelected(): boolean {
    return this.claudeRestoreTypes.includes(this.value.restoreAs);
  }

  constructor() {
    const mainService = inject(RemoteManagementWizardsService);

    super();

    this.mainService = mainService;

    const version = this.mainService.backupVersionUpdated ? +this.mainService.backupVersionUpdated.substring(0, 3) : 0;
    this.canRestoreInstantToVM = version >= VersionForVMInstantTypes;
    this.canRestoreToVM = version >= VersionForVMTypes;

    this.auth.currentUser$
      .pipe(
        first((user: Administrator) => !!user?.ProviderInfo),
        untilDestroyed(this)
      )
      .subscribe({ next: (user: Administrator) => (this.currentUser = user) });

    this.licensesFacade.hasCloudRestoreLicense$.pipe(untilDestroyed(this)).subscribe({
      next: (hasLicenses) => this.setRestoreToCloudPermissions(hasLicenses),
      error: () => this.setRestoreToCloudPermissions(false)
    });
  }

  ngOnInit(): void {
    this.initForm();

    this.stepService.hyperVNotFoundError$.pipe().subscribe({
      next: (error) => {
        this.stepForm.updateValueAndValidity();
        if (error && this.stepFocused) this.stepService.openErrorModal(error);
      }
    });

    if (this.isRestoreIbb) {
      this.stepService
        .pipeProperty('storage')
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (value) => {
            this.canRestoreToCloudStorage =
              value?.type !== StorageType.LocalFS && value?.type !== StorageType.Dedup && !value?.isPrivateStorage;
            this.updateCanRestoreToCloudStorage();
          }
        });

      this.updateFlagsIfIbbRestoreTypeWizard();
    }
  }

  initForm(): void {
    this.stepForm = new FormGroup<TypeDataStepValueForm>(
      {
        restoreAs: new FormControl(0),
        createAMI: new FormControl(false),
        drServerComputer: new FormControl(null),
        selectedFileFormat: new FormControl('', [Validators.required])
      },
      [this.hyperVNotFoundValidator.bind(this), this.drServerRequiredValidator.bind(this)]
    );

    this.initFormEvents();
  }

  drServerRequiredValidator(): ValidationErrors | null {
    if (!this.isDRServerRestore) return null;

    return this.selectedDRServer ? null : { drServerRequired: true };
  }

  hyperVNotFoundValidator(): ValidationErrors | null {
    return this.typesForValidate.includes(this.stepForm?.get('restoreAs').value) && this.stepService.hyperVNotFoundError$?.value
      ? { hyperVNotFound: true }
      : null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.selectedComputersCount) this.updateDataAfterChangeSelectedComputer(changes.selectedComputersCount.currentValue);
    if (
      changes?.stepFocused?.currentValue &&
      this.stepService.hyperVNotFoundError$.value &&
      this.typesForValidate.includes(this.stepForm?.get('restoreAs').value)
    ) {
      this.stepService.openErrorModal(this.stepService.hyperVNotFoundError$.value);
    }
  }

  ngOnDestroy(): void {
    this.stepService.hyperVNotFoundError$?.next(null);
  }

  updateForm(value: TypeDataStepValue): void {
    if (!value.selectedFileFormat && this.fileFormats.length) value.selectedFileFormat = this.fileFormats[0];

    this.updateCount++;

    this.stepForm.reset(value);

    this.activeIds = this.restoreAsPanelActiveMap[value.restoreAs] || [this.elementsSelector.id.physicalOrVirtualPanel];
    this.changeRestoreAsHandler(value.restoreAs);
  }

  forceValid(data: ForceValidType = null): void {
    if (
      this.typesForValidate.includes(this.stepForm?.get('restoreAs').value) &&
      this.stepService.hyperVNotFoundError$?.value &&
      (data.isNext || data.isSave)
    ) {
      this.stepService.openErrorModal(this.stepService.hyperVNotFoundError$?.value);
    }

    FormsUtil.triggerValidation(this.stepForm);
    this.resetValidStateForValidFields();
  }

  closeWizard(): void {
    this.mainService.closeAndReset();
  }

  panelHiddenHandler(id: string): void {
    if (id === this.activeId) this.activeId = null;
  }

  panelChangeHandler(value: { event: boolean; panelId: string }): void {
    this.activeId = value.panelId;
  }

  changeRestoreAsHandler(event: RestoreAsDiskEnum): void {
    this.toggleFormControls(['createAMI'], event === RestoreAsDiskEnum.EC2);
    this.toggleFormControls(['selectedFileFormat'], event === RestoreAsDiskEnum.Virtual);

    this.syncDRServerTarget();
    this.restoreAsChange.emit(event);
  }

  get isDRServerRestore(): boolean {
    return this.stepForm?.get('restoreAs')?.value === RestoreAsDiskEnum.InstantRestoreToDRServer;
  }

  get selectedDRServer(): Computer {
    return this.stepForm?.get('drServerComputer')?.value;
  }

  selectDRServerClickHandler(): void {
    this.modalFacade
      .showSelectComputerModal(this.drServerModalSettings())
      .pipe(take(1), untilDestroyed(this))
      .subscribe((result) => {
        this.stepForm.get('drServerComputer').setValue(result?.items?.[0] || null);
        this.syncDRServerTarget();
      });
  }

  getAzureRequest(): void {
    this.openRequestPermissionsModal(RestoreTargetEnum.Azure);
  }

  getEC2Request(): void {
    this.openRequestPermissionsModal(RestoreTargetEnum.EC2);
  }

  buyRestoreLicense(): void {
    this.currentUser?.ProviderInfo?.IsPostPayment
      ? this.licensesFacade.addServiceLicenseModal(MBSLicenseKind.Service, LicenseType.RestoreToCloud, true)
      : this.licensesFacade.proceed([LicenseType.RestoreToCloud]);
  }

  private drServerModalSettings(): ModalSettings {
    return {
      size: MbsSize.md,
      data: {
        items: [],
        filter: { os: [] },
        hiddenFilters: { platformType: PlatformTypes.DrLinux, appIds: [AgentType.Backup], online: true },
        disableOs: true,
        showComputerDetails: true,
        notSetResult: true,
        saveText: this.i18nPipe.transform('shared:buttons.ok'),
        selectedAll: false,
        multipleSelect: false,
        computersNotFound: this.i18nPipe.transform('shared:sidepanel-backup.backupStorages.browseStorage.computersRDNotFound'),
        excludeItem: null
      }
    };
  }

  // The chosen DR Server is where the restore runs, so it takes over as the wizard's target computer - every request
  // and the saved plan already read the hid from there
  private syncDRServerTarget(): void {
    const computer = this.isDRServerRestore ? this.selectedDRServer : null;

    this.mainService.targetComputerData = computer
      ? {
          computerName: computer.name || computer.displayName,
          email: computer.userAccount?.accountName,
          hid: computer.hid,
          toComputer: computer,
          userId: computer.userAccount?.id
        }
      : this.initialTargetComputerData;
  }

  private updateDataAfterChangeSelectedComputer(count: number): void {
    if (!this.stepForm || !this.isVMRestore) return;

    const control = this.stepForm.get('restoreAs');
    const needUpdate = count > 1 && control.value === RestoreAsDiskEnum.Virtual;

    if (!needUpdate) return;

    if (this.isCreate || this.updateCount > 1) {
      control.setValue(
        this.isRestoreProxmox
          ? RestoreAsDiskEnum.RestoreToProxmox
          : this.isRestoreHyperV
            ? RestoreAsDiskEnum.RestoreToHyperVAndImport
            : RestoreAsDiskEnum.RestoreToVMware
      );
    }

    this.restoreAsChange.emit(control.value);
  }

  private setRestoreToCloudPermissions(hasLicenses: boolean) {
    this.canRestoreToAzure = this.ability.can('read', PermissionsEnum.RestoreFromIBBToAzure);
    this.canRestoreToCloud = this.ability.can('read', PermissionsEnum.CloudRestore) && !this.ability.can('read', PermissionsEnum.B2Only);
    this.canRestoreToEC2 = this.ability.can('read', PermissionsEnum.RestoreFromIBBToEC2);
    this.hasRestoreToCloudLicense = hasLicenses;
    this.canBuyLicense = this.ability.can('read', PermissionsEnum.LicenseBuy);
  }

  private openRequestPermissionsModal(target: RestoreTargetEnum): void {
    const permission = target === RestoreTargetEnum.Azure ? PermissionsEnum.RestoreFromIBBToAzure : PermissionsEnum.RestoreFromIBBToEC2;

    this.licensesFacade.hasCloudRestoreLicense$.pipe(take(1)).subscribe((hasLicense) => {
      if (hasLicense || this.ability.can('read', permission)) return;

      const modal = this.modalService.openRef(RequestPermissionsModalComponent, { size: MbsSize.lg });
      modal.componentInstance.target = target;
    });
  }

  private updateCanRestoreToCloudStorage(): void {
    if (this.canRestoreToCloudStorage || (!this.isCloudStorageSelected && this.activeId !== this.elementsSelector.id.cloudPanel)) return;

    const restoreAs = this.isCloudStorageSelected ? RestoreAsDiskEnum.Physical : this.value.restoreAs;

    this.stepForm.get('restoreAs').setValue(restoreAs);

    this.restoreTypeStepAccordion.collapseAll();
    this.restoreTypeStepAccordion.expand(this.restoreAsPanelActiveMap[restoreAs]?.[0] || this.elementsSelector.id.physicalOrVirtualPanel);
  }

  private updateFlagsIfIbbRestoreTypeWizard(): void {
    if (!this.mainService.restoreParams) return;

    // const item = this.mainService.restoreParams?.item;
    // let storage: MyTreeElements = item?.parent as MyTreeElements;
    //
    // while (storage.parent && storage?.id !== storage.storageId && !storage.isStorage) {
    //   storage = storage.parent as MyTreeElements;
    // }

    this.canShowItemLvlPanel = true;
    // this.canRestoreToCloudStorage = !this.mainService.restoreParams.isLocalStorage && !value?.isPrivateStorage;
    this.canRestoreToCloudStorage = !this.mainService.restoreParams.isLocalStorage;
    this.updateCanRestoreToCloudStorage();
  }
}
